import React, { useState, useContext } from "react";
import UserList from "./UserList";
import CreateUserForm from "./CreateUserForm";
import EditUserForm from "./EditUserForm";
import { AuthContext } from "../../contexts/AuthContext";

const User = () => {
  const [activeTab, setActiveTab] = useState("users");
  const [userToEdit, setUserToEdit] = useState(null);
  const [hipaaAcknowledged, setHipaaAcknowledged] = useState(
    sessionStorage.getItem("hipaaUserMgmtAck") === "true"
  );
  const [showReminder, setShowReminder] = useState(true);
  const { user } = useContext(AuthContext);

  // Check if user has admin role
  const isAdmin = user?.role?.toLowerCase() === "admin";

  const handleEditClick = (user) => {
    setUserToEdit(user);
  };

  const handleFinishEdit = () => {
    setUserToEdit(null);
    setActiveTab("users");
  };

  const handleFinishCreate = () => {
    // After creating a user, go back to the list
    setActiveTab("users");
  };

  const handleHipaaAcknowledge = () => {
    setHipaaAcknowledged(true);
    sessionStorage.setItem("hipaaUserMgmtAck", "true");
  };

  // If not admin, show access denied message
  if (!isAdmin) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">
            <i className="bi bi-shield-x me-2"></i>
            Access Denied
          </h4>
          <p>You must be an administrator to access user management.</p>
          <hr />
          <p className="mb-0">
            Please contact your system administrator if you need access to this
            area.
          </p>
        </div>
      </div>
    );
  }

  // Show HIPAA acknowledgment on first access
  if (!hipaaAcknowledged) {
    return (
      <div className="container mt-4">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h4 className="mb-0">
              <i className="bi bi-shield-lock me-2"></i>
              HIPAA Compliance - User Management Access
            </h4>
          </div>
          <div className="card-body">
            <h5>Protected Health Information (PHI) Access Notice</h5>
            <p>
              You are about to access the User Management system which contains
              Personally Identifiable Information (PII) and may provide access
              to Protected Health Information (PHI).
            </p>

            <div className="alert alert-warning">
              <h6 className="alert-heading">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Important Responsibilities
              </h6>
              <ul className="mb-0">
                <li>
                  Access user information only for legitimate business purposes
                </li>
                <li>Follow the principle of minimum necessary access</li>
                <li>
                  Verify user identity before creating or modifying accounts
                </li>
                <li>Document authorization for role changes</li>
                <li>Report any suspected unauthorized access immediately</li>
                <li>
                  Never share login credentials or create generic accounts
                </li>
              </ul>
            </div>

            <div className="alert alert-info">
              <h6 className="alert-heading">
                <i className="bi bi-journal-text me-2"></i>
                Audit Logging Active
              </h6>
              <p className="mb-0">
                All actions in this system are logged and regularly audited.
                This includes:
              </p>
              <ul className="mb-0 mt-2">
                <li>User account creation, modification, and deletion</li>
                <li>Role and permission changes</li>
                <li>Password resets and account status changes</li>
                <li>All data views and exports</li>
              </ul>
            </div>

            <div className="mt-4">
              <h6>HIPAA Compliance Requirements:</h6>
              <ol>
                <li>
                  Ensure all users complete HIPAA training before granting
                  access
                </li>
                <li>
                  Implement strong password requirements (minimum 8 characters,
                  mixed case, numbers, symbols)
                </li>
                <li>Deactivate accounts promptly when employees leave</li>
                <li>Review user access rights quarterly</li>
                <li>Document all access authorization decisions</li>
              </ol>
            </div>

            <div className="form-check mt-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="acknowledgeHipaa"
                onChange={(e) => {
                  if (e.target.checked) {
                    handleHipaaAcknowledge();
                  }
                }}
              />
              <label className="form-check-label" htmlFor="acknowledgeHipaa">
                <strong>
                  I understand my responsibilities under HIPAA and agree to
                  comply with all privacy and security requirements when
                  managing user accounts.
                </strong>
              </label>
            </div>
          </div>
          <div className="card-footer text-muted">
            <small>
              <i className="bi bi-info-circle me-1"></i>
              This acknowledgment is required once per session. Violations of
              HIPAA regulations may result in civil and criminal penalties.
            </small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header with description */}
      <div className="mb-4">
        <h1>
          <i className="bi bi-people-fill me-2"></i>
          User Management
        </h1>
        <p className="text-muted">
          Manage system users, roles, and permissions in compliance with HIPAA
          requirements
        </p>

        {/* Quick compliance reminder */}
        {showReminder && (
          <div
            className="alert alert-info alert-dismissible fade show"
            role="alert"
          >
            <i className="bi bi-shield-check me-2"></i>
            <strong>Reminder:</strong> All user management activities are
            logged. Ensure you have proper authorization before making changes.
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setShowReminder(false)}
            ></button>
          </div>
        )}
      </div>

      {/* 🔝 Tabs always visible */}
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "users" ? "active" : ""}`}
            onClick={() => {
              setUserToEdit(null);
              setActiveTab("users");
            }}
          >
            <i className="bi bi-list me-1"></i>
            All Users
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "createUser" ? "active" : ""}`}
            onClick={() => {
              setUserToEdit(null);
              setActiveTab("createUser");
            }}
            disabled={userToEdit !== null} // Disable when editing
          >
            <i className="bi bi-person-plus me-1"></i>
            New User
          </button>
        </li>
      </ul>

      {/* 🔄 Content below tabs */}
      <div className="tab-content mt-3">
        {userToEdit ? (
          <EditUserForm user={userToEdit} onFinish={handleFinishEdit} />
        ) : activeTab === "users" ? (
          <UserList onEdit={handleEditClick} />
        ) : (
          <CreateUserForm onFinish={handleFinishCreate} />
        )}
      </div>

      {/* Footer with compliance info */}
      <div className="mt-5 p-3 bg-light rounded">
        <div className="row">
          <div className="col-md-4">
            <h6>
              <i className="bi bi-question-circle me-1"></i>
              Need Help?
            </h6>
            <small className="text-muted">
              Contact your HIPAA Security Officer for questions about user
              access policies.
            </small>
          </div>
          <div className="col-md-4">
            <h6>
              <i className="bi bi-book me-1"></i>
              Documentation
            </h6>
            <small className="text-muted">
              Refer to your organization's HIPAA policies for user management
              procedures.
            </small>
          </div>
          <div className="col-md-4">
            <h6>
              <i className="bi bi-exclamation-circle me-1"></i>
              Report Issues
            </h6>
            <small className="text-muted">
              Report suspected unauthorized access to security@yourorg.com
              immediately.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default User;
